import { ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation, signal, ViewChild } from "@angular/core";
import { S25Util } from "../../util/s25-util";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { ContactService } from "../../services/contact.service";
import { S25ItemI } from "../../pojo/S25ItemI";
import { MultiselectApi, MultiselectModelI } from "../s25-multiselect/s25.multiselect.component";
import { TaskService } from "../../services/task/task.service";
import { Task } from "../../pojo/Task";
import { Bind } from "../../decorators/bind.decorator";
import { S25MultiselectSearchCriteriaComponent } from "../s25-multiselect/multiselects/s25.multiselect.search.criteria.component";

@TypeManagerDecorator("s25-ng-create-todo")
@Component({
    selector: "s25-ng-create-todo",
    template: `
        @if (init) {
            <form class="taskFlexWrapper">
                <section class="taskColumn">
                    <label class="taskItem c-margin-bottom--half">
                        <b>Task Name:</b>
                        <input
                            type="text"
                            [(ngModel)]="task.taskName"
                            class="limit-width c-input"
                            max="40"
                            name="first"
                        />
                    </label>
                    <label class="taskItem c-margin-bottom--half">
                        <b>Comment:</b>
                        <textarea [(ngModel)]="task.taskComment" class="limit-width" name="comment"></textarea>
                    </label>
                    <label>
                        <b>Assigned By:</b>
                        {{ currentContact.itemName }}
                    </label>
                </section>
                <section class="taskColumn">
                    <label class="taskItem">
                        <b>Assigned To:</b>
                        <s25-ng-multiselect-search-criteria
                            [type]="'contacts'"
                            [selectedItems]="contactMultiselect.selectedItems"
                            [modelBean]="contactMultiselect"
                            [popoverOnBody]="true"
                            [popoverPlacement]="'bottom-left top-left top left'"
                        ></s25-ng-multiselect-search-criteria>
                    </label>
                    <label class="taskItem">
                        <b>Due Date:</b>
                        <s25-ng-editable-date [(val)]="task.dueDate" [alwaysEditing]="true"></s25-ng-editable-date>
                    </label>
                </section>
            </form>
            <span class="ngRed align-self-end">{{ errorMsg() }}</span>
            <s25-ng-button
                class="align-self-end"
                [type]="'primary'"
                [onClick]="createTask"
                [buttonText]="'Create'"
                [isLoading]
            ></s25-ng-button>
        } @else if (successMessage()) {
            <div class="ngCenterAlignText">
                <p class="ngBold">Success!</p>
                <p>{{ successMessage() }}</p>
                <button (click)="resetForm()" class="aw-button--primary">Create Another To Do Task</button>
            </div>
        }
    `,
    styles: `
        :host .taskFlexWrapper label,
        label b {
            display: block;
        }

        .taskItem textarea {
            height: 6em;
            width: 90%;
            max-width: 500px;
        }

        .taskItem input {
            width: 90%;
            max-width: 500px;
        }

        :host {
            padding: 1em 3em;
            display: flex;
            flex-direction: column;
        }

        :host button {
            align-self: flex-end;
        }

        :host .taskFlexWrapper {
            display: flex;
        }

        :host .taskFlexWrapper .taskColumn {
            align-items: flex-start;
            width: 50%;
            margin-left: 2em;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false,
})
export class CreateTodoComponent implements OnInit {
    @Input() eventId: number;

    @ViewChild(S25MultiselectSearchCriteriaComponent) multiSelect: S25MultiselectSearchCriteriaComponent;

    helpLink = "event_todo_create";
    errorMsg = signal("");
    successMessage = signal("");
    init = false;
    currentContact: S25ItemI;
    task: Task.Todo.Object = {
        taskName: "",
        taskComment: "",
        assignedToId: undefined,
        dueDate: new Date(),
        assignedById: undefined,
    };
    contactMultiselect: MultiselectModelI = {
        buttonText: "Select Contact",
        singleSelect: true,
        showResult: true,
        customFilterValue: "&is_r25user=1",
    };

    constructor(private changeDetector: ChangeDetectorRef) {}

    async getContact() {
        const resp = await ContactService.getCurrentContactSimple();
        return {
            itemId: resp?.contacts?.contact?.contact_id,
            itemName: resp?.contacts?.contact?.contact_name || "",
        };
    }

    async ngOnInit() {
        this.currentContact = await this.getContact();
        this.resetForm();

        this.init = true;
        this.changeDetector.detectChanges();
    }

    validate() {
        this.errorMsg.set("");
        if (!this.task.taskName) {
            this.errorMsg.set("Task name is required.");
            return false;
        }
        if (!this.task.assignedToId) {
            this.errorMsg.set("You must select a contact to assign the task to.");
            return false;
        }
        if (!this.task.dueDate) {
            this.errorMsg.set("Due date is required.");
            return false;
        }
        if (this.task.taskName.length > 40) {
            this.errorMsg.set("Task name cannot exceed 40 characters.");
            return false;
        }

        return true;
    }

    @Bind
    async createTask() {
        this.successMessage.set("");
        this.task.assignedToId = +this.contactMultiselect.selectedItems[0]?.itemId;
        if (!this.validate()) return;

        const [resp, err] = await S25Util.Maybe(TaskService.createEventTodo(this.task));
        if (err) {
            this.errorMsg.set(S25Util.errorText(err));
        }
        if (resp) {
            this.successMessage.set("Your To Do Task has been created.");
            this.init = false;
        }
        this.changeDetector.detectChanges();
    }

    resetForm() {
        this.buildContactBean();
        this.contactMultiselect.selectedItems = [{ ...this.currentContact }];
        Object.assign(this.task, {
            taskName: "",
            taskComment: "",
            assignedToId: +this.currentContact.itemId,
            dueDate: new Date(),
            assignedById: +this.currentContact.itemId,
            eventId: this.eventId,
        });
        this.init = true;

        this.successMessage.set("");
        this.errorMsg.set("");
        this.changeDetector.detectChanges();
    }

    @Bind
    buildContactBean() {
        const filter = {
            isPushed: false,
            label: "Event Contacts",
            action: async (bean: MultiselectModelI) => {
                filter.isPushed = !filter.isPushed;

                if (filter.isPushed && !bean.customFilterValue.includes("&event_id=")) {
                    this.contactMultiselect.customFilterValue += this.eventId ? "&event_id=" + this.eventId : "";
                } else {
                    this.contactMultiselect.customFilterValue = this.contactMultiselect.customFilterValue.replace(
                        /&event_id=\d+/g,
                        "",
                    );
                }

                await this.multiSelect.searchByTerm();
                MultiselectApi.refresh(document.body, this.contactMultiselect.uuid);
            },
        };

        this.contactMultiselect.customFilter = [filter]; //set the filter on the multiselect bean
    }
}
